/**
 * @file components/CustomFields.js
 * @description Customer Butler — dynamic custom-field UI (ADR-8). Renders form
 * inputs and read-only displays straight from a company's FieldDefinitions, so
 * whatever fields a company adds (Zoho-style) appear on the record everywhere
 * with zero code changes. Fields are grouped by their `section`.
 *
 * Exports:
 *   <CustomFieldsForm definitions values onChange />  — editable inputs
 *   <CustomFieldsDisplay definitions values />        — read-only key/value view
 */
import React from 'react';

const inputStyle = {
  width: '100%',
  padding: '0.5rem',
  border: '1px solid var(--color-border, #E5E7EB)',
  borderRadius: '6px',
  fontSize: '0.875rem',
  backgroundColor: 'var(--surface-card, #fff)',
  color: 'var(--text-primary, #111)',
  boxSizing: 'border-box',
};
const labelStyle = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 500,
  marginBottom: '0.25rem',
  color: 'var(--text-secondary, #6B7280)',
};

/** Group an array of field definitions by their section (preserving order). */
function groupBySection(definitions) {
  const groups = {};
  const order = [];
  for (const def of definitions) {
    const section = def.section || 'Details';
    if (!groups[section]) { groups[section] = []; order.push(section); }
    groups[section].push(def);
  }
  return order.map((s) => ({ section: s, fields: groups[s] }));
}

/** Render a single input appropriate to the field's data type. */
function FieldInput({ def, value, onChange }) {
  const v = value === undefined || value === null ? '' : value;
  const set = (val) => onChange(def.key, val);

  switch (def.dataType) {
    case 'textarea':
      return <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={v} onChange={(e) => set(e.target.value)} />;
    case 'number':
    case 'decimal':
    case 'currency':
      return <input style={inputStyle} type="number" step="any" value={v} onChange={(e) => set(e.target.value)} />;
    case 'date':
      return <input style={inputStyle} type="date" value={v ? String(v).slice(0, 10) : ''} onChange={(e) => set(e.target.value)} />;
    case 'datetime':
      return <input style={inputStyle} type="datetime-local" value={v} onChange={(e) => set(e.target.value)} />;
    case 'boolean':
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
          <input type="checkbox" checked={!!value} onChange={(e) => set(e.target.checked)} />
          <span style={{ color: 'var(--text-secondary, #6B7280)' }}>Yes</span>
        </label>
      );
    case 'dropdown': {
      const options = (def.options && def.options.values) || [];
      return (
        <select style={inputStyle} value={v} onChange={(e) => set(e.target.value)}>
          <option value="">-- Select --</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    case 'multiselect': {
      const options = (def.options && def.options.values) || [];
      const arr = Array.isArray(value) ? value : [];
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {options.map((o) => (
            <label key={o} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8125rem' }}>
              <input
                type="checkbox"
                checked={arr.includes(o)}
                onChange={(e) => set(e.target.checked ? [...arr, o] : arr.filter((x) => x !== o))}
              />
              {o}
            </label>
          ))}
        </div>
      );
    }
    case 'email':
      return <input style={inputStyle} type="email" value={v} onChange={(e) => set(e.target.value)} />;
    case 'phone':
      return <input style={inputStyle} type="tel" value={v} onChange={(e) => set(e.target.value)} />;
    case 'url':
      return <input style={inputStyle} type="url" value={v} onChange={(e) => set(e.target.value)} />;
    case 'formula':
      return <input style={{ ...inputStyle, fontStyle: 'italic', color: 'var(--text-tertiary, #9CA3AF)' }} value={v} readOnly placeholder="(calculated)" />;
    default:
      return <input style={inputStyle} type="text" value={v} onChange={(e) => set(e.target.value)} />;
  }
}

export function CustomFieldsForm({ definitions = [], values = {}, onChange }) {
  if (!definitions.length) return null;
  const groups = groupBySection(definitions.filter((d) => d.isActive !== false));
  return (
    <>
      {groups.map(({ section, fields }) => (
        <fieldset key={section} style={{ border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '6px', padding: '0.75rem', marginBottom: '1rem' }}>
          <legend style={{ fontWeight: 600, fontSize: '0.875rem', padding: '0 0.5rem' }}>{section}</legend>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
            {fields.map((def) => (
              <div key={def.key}>
                <label style={labelStyle}>{def.label}{def.required ? ' *' : ''}</label>
                <FieldInput def={def} value={values[def.key]} onChange={onChange} />
              </div>
            ))}
          </div>
        </fieldset>
      ))}
    </>
  );
}

export function CustomFieldsDisplay({ definitions = [], values = {} }) {
  const filled = definitions.filter((d) => d.isActive !== false && values[d.key] !== undefined && values[d.key] !== null && values[d.key] !== '');
  if (!filled.length) return null;
  const groups = groupBySection(filled);
  return (
    <>
      {groups.map(({ section, fields }) => (
        <div key={section} style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary, #6B7280)', marginBottom: '0.5rem' }}>{section}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
            {fields.map((def) => {
              let display = values[def.key];
              if (def.dataType === 'boolean') display = display ? 'Yes' : 'No';
              else if (Array.isArray(display)) display = display.join(', ');
              else if (def.dataType === 'date') display = String(display).slice(0, 10);
              return (
                <div key={def.key}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary, #9CA3AF)' }}>{def.label}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-primary, #111)' }}>{String(display)}</div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
}

export default CustomFieldsForm;
